/**
 * URL Recognition for Cloud Services
 * Detects and provides friendly labels for known cloud service URLs
 */

interface UrlInfo {
  label: string;
  service: 'aws' | 'azure' | 'datadog' | 'unknown';
}

/**
 * Recognize and label URLs from known cloud services
 */
export const recognizeUrl = (url: string): UrlInfo => {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();
    const pathname = urlObj.pathname;

    // AWS Console (console.aws.amazon.com or regional variants like us-east-1.console.aws.amazon.com)
    if (hostname.includes('console.aws.amazon.com')) {
      // CloudWatch Logs
      if (pathname.includes('cloudwatch')) {
        return { label: 'AWS CloudWatch Logs', service: 'aws' };
      }
      // Lambda Console
      if (pathname.includes('lambda')) {
        return { label: 'AWS Lambda Console', service: 'aws' };
      }
      // S3 Console
      if (pathname.includes('s3')) {
        return { label: 'AWS S3 Console', service: 'aws' };
      }
      // Step Functions
      if (pathname.includes('states')) {
        return { label: 'AWS Step Functions', service: 'aws' };
      }
      // ECS Console
      if (pathname.includes('ecs')) {
        return { label: 'AWS ECS Console', service: 'aws' };
      }
      // Generic AWS Console
      return { label: 'AWS Console', service: 'aws' };
    }

    // Other AWS services (*.amazonaws.com)
    if (hostname.includes('amazonaws.com')) {
      return { label: 'AWS', service: 'aws' };
    }

    // Azure Services
    if (hostname.includes('azure.com') || hostname.includes('microsoft.com')) {
      // Azure Portal
      if (hostname.includes('portal.azure.com')) {
        if (pathname.includes('functionapp')) {
          return { label: 'Azure Functions', service: 'azure' };
        }
        if (pathname.includes('logicapp')) {
          return { label: 'Azure Logic Apps', service: 'azure' };
        }
        if (pathname.includes('workspace')) {
          return { label: 'Azure Log Analytics', service: 'azure' };
        }
        if (pathname.includes('insights')) {
          return { label: 'Azure Application Insights', service: 'azure' };
        }
        return { label: 'Azure Portal', service: 'azure' };
      }
      // Azure DevOps
      if (hostname.includes('dev.azure.com') || hostname.includes('visualstudio.com')) {
        return { label: 'Azure DevOps', service: 'azure' };
      }
      return { label: 'Azure', service: 'azure' };
    }

    // Datadog
    if (hostname.includes('datadoghq.com') || hostname.includes('datadoghq.eu')) {
      if (pathname.includes('logs')) {
        return { label: 'Datadog Logs', service: 'datadog' };
      }
      if (pathname.includes('apm')) {
        return { label: 'Datadog APM', service: 'datadog' };
      }
      if (pathname.includes('dashboard')) {
        return { label: 'Datadog Dashboard', service: 'datadog' };
      }
      if (pathname.includes('monitors')) {
        return { label: 'Datadog Monitors', service: 'datadog' };
      }
      if (pathname.includes('infrastructure')) {
        return { label: 'Datadog Infrastructure', service: 'datadog' };
      }
      return { label: 'Datadog', service: 'datadog' };
    }

    // Unknown service - return the URL as-is
    return { label: url, service: 'unknown' };
  } catch {
    // Invalid URL - return as-is
    return { label: url, service: 'unknown' };
  }
};
